import { Button } from "hec/components/cta";
import { getDetermineRouteTypeAndUrl } from "hec/hooks/useDetermineRouteTypeAndUrl";
import PropTypes from "prop-types";
import { type CSSProperties, type ReactNode, Suspense } from "react";
import { Await } from "react-router";

import type { RegisteredComponent } from "@builder.io/sdk-react";

import { asHighlightColor, opaqueRgbToHex } from "~/hec/utils/color-contrast";
import { type SiteId } from "~/hec/utils/site-id";
import { cn } from "~/lib/utils";
import { useRootLayoutData } from "~/root";

import type { ModelReference } from "../BuilderReferenceContent";
import {
  coreButtonInputs,
  coreButtonPropTypes,
  hasCustomUrl,
  withCustomUrlInputs,
} from "./cta.helper";
import useFetchPageDataForCta, {
  type BuilderContent,
} from "./useFetchPageDataForCta";

// Define types for the props

interface QueryParam {
  name: string;
  value: string;
}

interface BuilderLinkProps {
  variant?: "normal" | "bold" | "navLink";
  disabled?: boolean;
  /** Optional pill label rendered after the link text (e.g. "New"). */
  badge?: string;
  /** Optional CSS color for the badge pill; empty keeps the brand purple. */
  badgeBackgroundColor?: string;
  /**
   * `navLink` only (COM-581). Optional row background; the text colour is
   * derived (white / navy) for AA contrast by the same helper the BM-driven
   * category rows use. Builder-authored links ONLY — the `MainNavigation`
   * block's category rows take their colour from BM and ignore this.
   */
  backgroundColor?: string;
  openInNewTab?: boolean;
  children?: ReactNode;
  attributes?: {
    className?: string;
    /** Builder editor positioning etc. — merged under the navLink highlight. */
    style?: CSSProperties;
    [key: string]: string | number | boolean | CSSProperties | undefined;
  };
  clickFunction: string;
  scrollTargetId?: string;
  isInternalLink?: boolean;
  isRevShare?: boolean;
  pageReference?: ModelReference;
  queryParams?: QueryParam[];
  externalLinkReference?: ModelReference;
  /** Author-typed URL; when non-empty it overrides the references (COM-214). */
  customUrl?: string;
  /** Routes `customUrl` as an SFRA page (full-document load). */
  customUrlIsSfra?: boolean;
  "data-testid"?: string;
}

const BuilderLink = ({
  variant,
  disabled,
  badge,
  badgeBackgroundColor,
  backgroundColor,
  openInNewTab,
  children,
  attributes,
  clickFunction,
  scrollTargetId,
  isInternalLink,
  isRevShare,
  pageReference,
  queryParams,
  externalLinkReference,
  customUrl,
  customUrlIsSfra,
  "data-testid": dataTestId,
}: BuilderLinkProps) => {
  // Builder's `attributes.style` can carry editor positioning — merge, never
  // clobber (same as `how-it-works.tsx`).
  const {
    className: classNameAttribute,
    style: styleAttribute,
    ...restAttributes
  } = attributes || {};

  // A non-empty customUrl overrides the page reference — skip the reference
  // fetch entirely (hook still runs unconditionally per the hooks rule). With
  // both refs undefined `isResolved` is true, so the Suspense/Await path below
  // is never entered for a typed URL. Same pattern as `linkWrapper.tsx`.
  const skipReferences = hasCustomUrl(customUrl);
  const { pageRef, externalLinkRef, isResolved } = useFetchPageDataForCta({
    isInternalLink,
    pageReference: skipReferences ? undefined : pageReference,
    externalLinkReference: skipReferences ? undefined : externalLinkReference,
  });

  const { site } = useRootLayoutData();

  // The badge is a prop (not a Builder child) so it renders inside the single
  // anchor the Button emits — avoids the nested-anchor break the old hand-built
  // Learning Center link hit on Workers. The default pill color is the fixed
  // brand purple production uses (not the per-site `accent` token, which would
  // drift); an author-picked "Badge background color" replaces it, and a blank
  // or unset value keeps the class-based default.
  const customBadgeColor = badgeBackgroundColor?.trim() || undefined;
  const linkContent = (
    <>
      {children}
      {badge ? (
        <span
          className={cn(
            "ml-[15px] inline-flex shrink-0 items-center whitespace-nowrap rounded-full px-[15px] pt-[2px] font-grotesque text-[0.875rem] font-light text-white",
            !customBadgeColor && "bg-[rgb(73,56,142)]",
          )}
          style={
            customBadgeColor ? { backgroundColor: customBadgeColor } : undefined
          }
          data-testid="link-badge"
        >
          {badge}
        </span>
      ) : null}
    </>
  );

  // The Builder Text child renders as a block-level <div>, so on a plain
  // inline anchor the pill wrapped onto its own line under the label. Only the
  // account popover looked right, and only because app.css force-flexes that
  // one anchor. With a badge the anchor lays itself out as a flex row so the
  // pill sits beside the text everywhere; `navLink` stays full-width (`flex`)
  // to keep its block hover surface, the text variants stay inline-level.
  // `flex-wrap` lets the pill drop below a label that cannot shrink any
  // further in a narrow column instead of overflowing it. `.hec-prose p` has
  // had no margin since COM-884; the zeroing stays as a guard so a later
  // prose margin can't push the label off the pill's centre.
  const BADGE_ROW = "items-center flex-wrap [&_.hec-prose_p]:my-0";
  const badgeLayoutClassName = badge
    ? variant === "navLink"
      ? `flex ${BADGE_ROW}`
      : `inline-flex ${BADGE_ROW}`
    : undefined;
  // COM-581 navLink row highlight. The Builder colour picker emits
  // `rgba(r, g, b, 1)`, so normalise an opaque value to hex before the shared
  // strict-hex helper; a translucent, low-contrast or malformed value falls
  // back to the default row (fail closed). Inline `style` wins over the cva
  // `hover:bg-accent hover:text-white`, so the hover becomes a slight darken
  // of the authored colour, matching the BM-driven category rows.
  const highlight =
    variant === "navLink"
      ? asHighlightColor(opaqueRgbToHex(backgroundColor))
      : undefined;
  const linkStyle: CSSProperties | undefined =
    highlight || styleAttribute
      ? {
          ...styleAttribute,
          ...(highlight && {
            backgroundColor: highlight.backgroundColor,
            color: highlight.textColor,
          }),
        }
      : undefined;
  const linkClassName = cn(
    classNameAttribute,
    badgeLayoutClassName,
    highlight &&
      "rounded-lg transition-[filter] hover:brightness-90 focus-visible:brightness-90",
  );

  // ONE definition of the resolved link, shared by both render paths below.
  // Keeping a second, hand-maintained copy of this JSX as the Suspense fallback
  // is precisely what COM-198 was: React SSR'd the fallback into `<main>`,
  // streamed this element in after `</footer>`, and shipped both.
  const renderResolvedLink = (
    resolvedPageRef: BuilderContent["data"] | undefined,
    resolvedExternalLinkRef: BuilderContent["data"] | undefined,
  ) => {
    const { routeType, url } = getDetermineRouteTypeAndUrl({
      clickFunction,
      isInternalLink,
      pageReference: resolvedPageRef,
      externalLinkReference: resolvedExternalLinkRef,
      customUrl,
      customUrlIsSfra,
      siteId: site.id as SiteId,
    });

    return (
      <Button
        ctaType="link"
        variant={variant}
        disabled={disabled}
        openInNewTab={openInNewTab}
        clickFunction={clickFunction}
        scrollTargetId={scrollTargetId}
        routeType={routeType ?? undefined}
        url={url!} // TODO: fix this nullish assertion
        queryParams={queryParams}
        className={linkClassName}
        isRevShare={isRevShare}
        {...restAttributes}
        style={linkStyle}
        data-testid={dataTestId || "link-Link"}
      >
        {linkContent}
      </Button>
    );
  };

  // Fast path: the reference was inlined server-side, so there is nothing to
  // await. No Suspense boundary means no pending marker, no duplicated subtree,
  // and no `$RC()` swap for this CTA.
  if (isResolved) {
    return renderResolvedLink(
      pageRef as BuilderContent["data"] | undefined,
      externalLinkRef as BuilderContent["data"] | undefined,
    );
  }

  // Streaming fallback, unchanged: a reference that failed to resolve or missed
  // the loader's resolve budget still renders correctly, just at the old cost.
  // The fallback stays a full-size disabled Button on purpose — it is what holds
  // the layout stable during the streaming window, and CLS on this page is
  // currently 0.000 (COM-157/186/187). Do not shrink it to a skeleton or null.
  return (
    <Suspense
      fallback={
        <Button
          ctaType="link"
          variant={variant}
          disabled={true}
          className={linkClassName}
          isRevShare={isRevShare}
          clickFunction=""
          {...restAttributes}
          style={linkStyle}
          data-testid={dataTestId || "link-Link-error"}
        >
          {linkContent}
        </Button>
      }
    >
      <Await resolve={pageRef}>
        {resolvedPageRef => (
          <Await resolve={externalLinkRef}>
            {resolvedExternalLinkRef =>
              renderResolvedLink(resolvedPageRef, resolvedExternalLinkRef)
            }
          </Await>
        )}
      </Await>
    </Suspense>
  );
};

BuilderLink.propTypes = {
  className: PropTypes.string,
  variant: PropTypes.string,
  disabled: PropTypes.bool,
  badge: PropTypes.string,
  badgeBackgroundColor: PropTypes.string,
  backgroundColor: PropTypes.string,
  ...coreButtonPropTypes,
};

export const LinkDefinition = {
  component: BuilderLink,
  image: "https://unpkg.com/css.gg@2.0.0/icons/svg/link.svg",
  name: "Link",
  override: true,
  noWrap: true,
  canHaveChildren: true,
  defaultChildren: [
    {
      "@type": "@builder.io/sdk:Element",
      component: {
        name: "Text",
        options: { text: "Click me" },
      },
    },
  ],
  childRequirements: {
    message: "You can only put Text and Icons into Link component",
    query: {
      "component.name": { $in: ["Text", "IconPicker", "Heading"] },
    },
  },
  inputs: [
    {
      name: "variant",
      type: "string",
      enum: ["normal", "bold", "navLink"],
      defaultValue: "normal",
    },
    {
      name: "disabled",
      type: "boolean",
      defaultValue: false,
    },
    {
      name: "badge",
      friendlyName: "Badge",
      type: "string",
      required: false,
      helperText:
        'Optional pill rendered after the link text (e.g. "New"). Leave empty for no badge.',
    },
    {
      name: "badgeBackgroundColor",
      friendlyName: "Badge background color",
      type: "color",
      required: false,
      showIf: `!!options.get('badge')`,
      helperText:
        "Optional. Background color of the badge pill. Leave empty to keep the default brand purple.",
    },
    {
      name: "backgroundColor",
      friendlyName: "Row background color",
      type: "color",
      required: false,
      showIf: `options.get('variant') === 'navLink'`,
      helperText:
        "Nav rows only. Text turns white or navy automatically for contrast. Low-contrast or transparent colors are ignored (default styling). Category rows from the MainNavigation block take their color from Business Manager instead.",
    },
    // Custom URL + "Is SFRA" spliced in after "isInternalLink" (COM-214).
    ...withCustomUrlInputs(coreButtonInputs),
  ],
} satisfies RegisteredComponent;
